import { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, mediaUrl } from "../../lib/api";
import { RichEditor } from "../RichEditor";
import { AIAssistDialog } from "../AIAssistDialog";
import { ArrowLeft, Save, Globe, Image as ImageIcon, Loader2, Trash2, Sparkles, Plus } from "lucide-react";

const slugify = (s) =>
  s.toLowerCase().normalize("NFKD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9\s-]/g, "").trim().replace(/\s+/g, "-").replace(/-+/g, "-");

export default function AdminArticleEditor() {
  const { id } = useParams();
  const isNew = !id || id === "new";
  const navigate = useNavigate();

  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState({
    title: "", slug: "", category: "Partners", status: "draft",
    excerpt: "", content: "", cover_image: "",
    seo_title: "", seo_description: "",
    card_title: "", card_excerpt: "", social_links: [],
  });
  const [loaded, setLoaded] = useState(isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [coverUploading, setCoverUploading] = useState(false);
  const [imgUploading, setImgUploading] = useState(false);
  const slugTouched = useRef(false);
  const coverInput = useRef(null);
  const editorRef = useRef(null);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiMode, setAiMode] = useState("outline");
  const [aiDefaultText, setAiDefaultText] = useState("");

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data.categories));
    if (!isNew) {
      api.get(`/admin/articles/${id}`).then((r) => {
        setForm({
          title: r.data.title || "",
          slug: r.data.slug || "",
          category: r.data.category || "Partners",
          status: r.data.status || "draft",
          excerpt: r.data.excerpt || "",
          content: r.data.content || "",
          cover_image: r.data.cover_image || "",
          seo_title: r.data.seo_title || "",
          seo_description: r.data.seo_description || "",
          card_title: r.data.card_title || "",
          card_excerpt: r.data.card_excerpt || "",
          social_links: r.data.social_links || [],
        });
        slugTouched.current = true;
        setLoaded(true);
      }).catch(() => { setError("Article not found"); setLoaded(true); });
    }
  }, [id, isNew]);

  const onTitleChange = (e) => {
    const v = e.target.value;
    setForm((p) => ({ ...p, title: v, slug: slugTouched.current ? p.slug : slugify(v) }));
  };

  const uploadFile = async (file, folder) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("folder", folder);
    const r = await api.post("/admin/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return r.data;
  };

  const onCoverUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCoverUploading(true);
    try {
      const rec = await uploadFile(file, "articles/covers");
      setForm((p) => ({ ...p, cover_image: rec.media_id }));
    } catch (err) {
      setError("Cover upload failed");
    } finally { setCoverUploading(false); if (coverInput.current) coverInput.current.value = ""; }
  };

  const onInsertImage = async () => {
    return new Promise((resolve) => {
      const input = document.createElement("input");
      input.type = "file";
      input.accept = "image/*";
      input.onchange = async () => {
        const file = input.files?.[0];
        if (!file) return resolve(null);
        setImgUploading(true);
        try {
          const rec = await uploadFile(file, "articles/inline");
          resolve(mediaUrl(rec.media_id));
        } catch (_) { resolve(null); }
        finally { setImgUploading(false); }
      };
      input.click();
    });
  };

  const save = async (publish = null) => {
    setError("");
    setSaving(true);
    const payload = { ...form };
    payload.social_links = (form.social_links || []).filter((l) => l.label?.trim() && l.url?.trim());
    if (publish === true) payload.status = "published";
    if (publish === false) payload.status = "draft";
    if (!payload.title.trim()) { setError("Title is required"); setSaving(false); return; }
    if (!payload.slug.trim()) payload.slug = slugify(payload.title);
    try {
      let res;
      if (isNew) {
        res = await api.post("/admin/articles", payload);
        navigate(`/admin/articles/${res.data.article_id}`, { replace: true });
      } else {
        res = await api.put(`/admin/articles/${id}`, payload);
      }
      setForm((p) => ({ ...p, status: res.data.status }));
    } catch (err) {
      setError(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  if (!loaded) return <p className="text-aubergine/55 font-mono text-xs">Loading…</p>;

  return (
    <div data-testid="admin-article-editor">
      <button onClick={() => navigate("/admin/articles")} data-testid="editor-back" className="inline-flex items-center text-aubergine/65 hover:text-aubergine font-mono text-[11px] uppercase tracking-[0.15em] mb-6">
        <ArrowLeft className="mr-2 h-3.5 w-3.5" /> All articles
      </button>

      <header className="mb-6 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-champagne mb-2">{isNew ? "New article" : "Edit article"}</p>
          <h1 className="font-serif text-3xl text-aubergine">{form.title || "Untitled"}</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-mono uppercase tracking-wider ${form.status === "published" ? "bg-champagne/15 text-aubergine" : "bg-aubergine/10 text-aubergine/65"}`}>
            <span className={`h-1.5 w-1.5 rounded-full ${form.status === "published" ? "bg-champagne" : "bg-aubergine/40"}`} />
            {form.status === "published" ? "Published" : "Draft"}
          </span>
          <button onClick={() => save(false)} disabled={saving} data-testid="editor-save-draft" className="inline-flex items-center gap-2 rounded-md border border-aubergine/20 hover:bg-aubergine/5 px-3 py-2 font-mono text-xs disabled:opacity-60">
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
            Save draft
          </button>
          <button onClick={() => save(true)} disabled={saving} data-testid="editor-publish" className="inline-flex items-center gap-2 rounded-md bg-aubergine text-bone hover:bg-aubergine/90 px-3 py-2 font-mono text-xs disabled:opacity-60">
            <Globe className="h-3.5 w-3.5" />
            {form.status === "published" ? "Update" : "Publish"}
          </button>
        </div>
      </header>

      {error && <div className="mb-4 rounded-md border border-signal-red/40 bg-signal-red/10 px-4 py-2 text-sm text-signal-red" data-testid="editor-error">{error}</div>}

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-5">
          <Field label="Title">
            <input data-testid="editor-input-title" value={form.title} onChange={onTitleChange} className={inputCls} placeholder="Article title" />
          </Field>
          <Field label="Slug" hint="URL: /articles/your-slug">
            <input data-testid="editor-input-slug" value={form.slug} onChange={(e) => { slugTouched.current = true; setForm((p) => ({ ...p, slug: slugify(e.target.value) })); }} className={`${inputCls} font-mono text-xs`} placeholder="article-slug" />
          </Field>
          <Field label="Excerpt" hint="Short summary shown on the listing page (1-2 sentences).">
            <textarea data-testid="editor-input-excerpt" value={form.excerpt} onChange={(e) => setForm((p) => ({ ...p, excerpt: e.target.value }))} className={`${inputCls} resize-none`} rows={2} />
          </Field>
          <Field label="Content">
            {imgUploading && <p className="mb-2 text-xs text-aubergine/65 inline-flex items-center gap-2"><Loader2 className="h-3 w-3 animate-spin" /> Uploading image…</p>}
            <RichEditor
              value={form.content}
              onChange={(html) => setForm((p) => ({ ...p, content: html }))}
              onInsertImage={onInsertImage}
              editorRef={editorRef}
              onAIOutline={() => { setAiMode("outline"); setAiDefaultText(""); setAiOpen(true); }}
              onAIRephrase={(selected) => { setAiMode("rephrase"); setAiDefaultText(selected || ""); setAiOpen(true); }}
            />
          </Field>
        </div>

        <aside className="space-y-5">
          <div className="rounded-lg border border-aubergine/10 bg-white p-5">
            <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-aubergine/55 mb-3">Settings</p>
            <Field label="Category">
              <select data-testid="editor-input-category" value={form.category} onChange={(e) => setForm((p) => ({ ...p, category: e.target.value }))} className={inputCls}>
                {categories.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
          </div>

          <div className="rounded-lg border border-aubergine/10 bg-white p-5">
            <div className="flex items-center justify-between mb-3">
              <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-aubergine/55">Cover image</p>
              <button
                onClick={() => { setAiMode("cover"); setAiOpen(true); }}
                data-testid="editor-cover-ai-generate"
                className="inline-flex items-center gap-1 rounded-md border border-champagne/40 bg-champagne/10 px-2 py-1 font-mono text-[10px] uppercase tracking-wider text-champagne hover:bg-champagne/20 transition-colors"
              >
                <Sparkles className="h-3 w-3" /> AI generate
              </button>
            </div>
            {form.cover_image ? (
              <div className="relative rounded-md overflow-hidden border border-aubergine/10">
                <img src={mediaUrl(form.cover_image)} alt="cover" className="w-full h-auto" />
                <button onClick={() => setForm((p) => ({ ...p, cover_image: "" }))} data-testid="editor-cover-remove" className="absolute top-2 right-2 inline-flex items-center gap-1 rounded-md bg-white/90 px-2 py-1 text-xs hover:bg-white">
                  <Trash2 className="h-3 w-3" /> Remove
                </button>
              </div>
            ) : (
              <button onClick={() => coverInput.current?.click()} data-testid="editor-cover-upload" disabled={coverUploading} className="w-full border-2 border-dashed border-aubergine/15 rounded-md p-6 text-aubergine/55 hover:border-champagne hover:text-champagne transition-colors flex flex-col items-center gap-2">
                {coverUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImageIcon className="h-5 w-5" />}
                <span className="text-xs">Upload cover image</span>
              </button>
            )}
            <input ref={coverInput} type="file" accept="image/*" onChange={onCoverUpload} className="hidden" />
          </div>

          <div className="rounded-lg border border-aubergine/10 bg-white p-5">
            <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-aubergine/55 mb-3">Listing card</p>
            <Field label="Card headline" hint="Short headline for the /articles card. Defaults to the title.">
              <input data-testid="editor-input-card-title" value={form.card_title} onChange={(e) => setForm((p) => ({ ...p, card_title: e.target.value }))} className={inputCls} placeholder={form.title} />
            </Field>
            <Field label="Card summary" hint="One or two lines on the card. Defaults to the excerpt.">
              <textarea data-testid="editor-input-card-excerpt" value={form.card_excerpt} onChange={(e) => setForm((p) => ({ ...p, card_excerpt: e.target.value }))} className={`${inputCls} resize-none`} rows={3} placeholder={form.excerpt} />
            </Field>
          </div>

          <div className="rounded-lg border border-aubergine/10 bg-white p-5">
            <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-aubergine/55 mb-3">Social posts</p>
            <p className="mb-3 text-[11px] text-aubergine/55">Shown at the end of the post, under "Also posted on".</p>
            <div className="space-y-2">
              {(form.social_links || []).map((l, i) => (
                <div key={i} className="flex items-start gap-2">
                  <input
                    value={l.label}
                    onChange={(e) => setForm((p) => ({ ...p, social_links: p.social_links.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)) }))}
                    data-testid={`editor-social-label-${i}`}
                    className={`${inputCls} w-28 flex-none`}
                    placeholder="X"
                  />
                  <input
                    value={l.url}
                    onChange={(e) => setForm((p) => ({ ...p, social_links: p.social_links.map((x, j) => (j === i ? { ...x, url: e.target.value } : x)) }))}
                    data-testid={`editor-social-url-${i}`}
                    className={`${inputCls} font-mono text-xs`}
                    placeholder="https://x.com/..."
                  />
                  <button
                    onClick={() => setForm((p) => ({ ...p, social_links: p.social_links.filter((_, j) => j !== i) }))}
                    data-testid={`editor-social-remove-${i}`}
                    className="mt-1.5 text-aubergine/45 hover:text-signal-red"
                    aria-label="Remove social link"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
            <button
              onClick={() => setForm((p) => ({ ...p, social_links: [...(p.social_links || []), { label: "X", url: "" }] }))}
              data-testid="editor-social-add"
              className="mt-3 inline-flex items-center gap-1.5 rounded-md border border-aubergine/20 hover:bg-aubergine/5 px-2.5 py-1.5 font-mono text-[11px] uppercase tracking-wider"
            >
              <Plus className="h-3 w-3" /> Add link
            </button>
          </div>

          <div className="rounded-lg border border-aubergine/10 bg-white p-5">
            <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-aubergine/55 mb-3">SEO</p>
            <Field label="SEO Title">
              <input data-testid="editor-input-seo-title" value={form.seo_title} onChange={(e) => setForm((p) => ({ ...p, seo_title: e.target.value }))} className={inputCls} placeholder={form.title} />
            </Field>
            <Field label="SEO Description">
              <textarea data-testid="editor-input-seo-description" value={form.seo_description} onChange={(e) => setForm((p) => ({ ...p, seo_description: e.target.value }))} className={`${inputCls} resize-none`} rows={3} placeholder={form.excerpt} />
            </Field>
          </div>
        </aside>
      </div>

      <AIAssistDialog
        open={aiOpen}
        onClose={() => setAiOpen(false)}
        mode={aiMode}
        defaultText={aiDefaultText}
        articleTitle={form.title}
        articleCategory={form.category}
        onApplyText={(payload) => {
          const editor = editorRef.current;
          if (!editor) return;
          if (aiMode === "outline") {
            // Append at end (or insert at cursor if empty selection)
            const html = payload;
            editor.chain().focus().insertContent(html).run();
          } else if (aiMode === "rephrase") {
            const { from, to } = editor.state.selection;
            if (from !== to) {
              editor.chain().focus().deleteSelection().insertContent(payload).run();
            } else {
              editor.chain().focus().insertContent(payload).run();
            }
          }
        }}
        onApplyCover={(media) => {
          setForm((p) => ({ ...p, cover_image: media.media_id }));
        }}
      />
    </div>
  );
}

const inputCls = "w-full rounded-md border border-aubergine/15 bg-white px-3 py-2 text-sm outline-none focus:border-champagne transition-colors";

function Field({ label, hint, children }) {
  return (
    <div className="mb-3 last:mb-0">
      <label className="block text-xs font-medium text-aubergine mb-1.5">{label}</label>
      {children}
      {hint && <p className="mt-1 text-[11px] text-aubergine/55">{hint}</p>}
    </div>
  );
}
